import React, { useState, useCallback } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { styled } from '@mui/material';
import { Colors } from 'library';
import { RModal, RModalFooter } from './RModal';
import { RPillButton } from './RPillButton';

interface RImageCropModalProps {
    imageSrc: string;
    aspect: number;
    title?: string;
    onClose: () => void;
    onCropComplete: (croppedFile: File) => void;
}

export function RImageCropModal({
    imageSrc,
    aspect,
    title = 'Crop Image',
    onClose,
    onCropComplete,
}: RImageCropModalProps) {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

    const handleCropComplete = useCallback((_: Area, croppedPixels: Area) => {
        setCroppedAreaPixels(croppedPixels);
    }, []);

    const handleSave = async () => {
        if (!croppedAreaPixels) return;
        const file = await getCroppedImageFile(imageSrc, croppedAreaPixels);
        onCropComplete(file);
    };

    return (
        <RModal title={title} onClose={onClose} width={600}>
            <CropContainer>
                <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={aspect}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={handleCropComplete}
                />
            </CropContainer>
            <ZoomRow>
                <ZoomLabel>Zoom</ZoomLabel>
                <ZoomSlider
                    type="range"
                    min={1}
                    max={3}
                    step={0.1}
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                />
            </ZoomRow>
            <RModalFooter>
                <RPillButton variant="secondary" onClick={onClose}>
                    Cancel
                </RPillButton>
                <RPillButton variant="primary" onClick={handleSave}>
                    Save
                </RPillButton>
            </RModalFooter>
        </RModal>
    );
}

async function getCroppedImageFile(imageSrc: string, crop: Area): Promise<File> {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;

    canvas.width = crop.width;
    canvas.height = crop.height;

    ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);

    const hasAlpha = detectAlpha(ctx, canvas.width, canvas.height);
    const type = hasAlpha ? 'image/png' : 'image/jpeg';
    const ext = hasAlpha ? 'png' : 'jpg';

    return new Promise((resolve) => {
        canvas.toBlob((blob) => {
            resolve(new File([blob!], `cropped.${ext}`, { type }));
        }, type, type === 'image/jpeg' ? 0.9 : undefined);
    });
}

function detectAlpha(ctx: CanvasRenderingContext2D, width: number, height: number): boolean {
    const data = ctx.getImageData(0, 0, width, height).data;
    for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 255) return true;
    }
    return false;
}

function createImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
}

const CropContainer = styled('div')({
    position: 'relative',
    width: '100%',
    height: 300,
    backgroundColor: Colors.faintGrey,
    borderRadius: 8,
    overflow: 'hidden',
});

const ZoomRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 12,
});

const ZoomLabel = styled('span')({
    fontSize: 13,
    fontWeight: 600,
    color: Colors.darkNavy,
    whiteSpace: 'nowrap',
});

const ZoomSlider = styled('input')({
    flex: 1,
    accentColor: Colors.darkNavy,
});
